import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { formatInTimeZone } from "date-fns-tz";
import { useSnackbarContext } from "components/snackbar";
import { useModalContext } from "components/modal";
import SafeArea from "components/safe-area";
import FlatContainer from "components/container/flat";
import Table from "components/table";
import { Button, OutlineButton, SubtleButton } from "components/button";
import { Status } from "components/status";
import { ICampaign, CampaignStatus, CampaignType } from "./types/ICampaign";
import { getCampaignsPage, deleteCampaign, resetCampaign, cloneCampaign } from "./api/campaigns";
import type { CampaignsPage } from "./api/campaigns";

import ConfirmActionModal from "./components/ConfirmActionModal";
import SendingProgress from "./components/SendingProgress";
/** Rows per page of the list (the server caps at 100). */
export const CAMPAIGNS_PAGE_SIZE = 25;

const STATUS_COLORS: Record<CampaignStatus, string> = {
  draft: "180,180,180",
  scheduled: "59,130,246",
  sending: "234,179,8",
  sent: "0,200,100",
  failed: "239,68,68",
  cancelled: "160,160,160",
};

/**
 * Send time of a scheduled campaign, rendered in the schedule's timezone
 * via date-fns-tz (the app's Intl monkey-patch would override the timezone
 * on native formatters). Defensive: a bad stored timezone must never crash
 * the list — fall back to the raw UTC instant.
 */
function formatScheduleCell(schedule: {
  scheduledAt: string;
  timezone: string;
}): string {
  try {
    return formatInTimeZone(
      new Date(schedule.scheduledAt),
      schedule.timezone,
      "MMM d, h:mm a"
    );
  } catch {
    return `${schedule.scheduledAt} (UTC)`;
  }
}

/**
 * The message the API attached to a failed request, if it sent one.
 * Reset answers 409/400 with `{ success: false, error: "<why>" }`.
 */
export function apiErrorMessage(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return null;
  }
  const { response } = error as { response?: { data?: { error?: unknown } } };
  const message = response?.data?.error;
  return typeof message === "string" && message.trim() !== "" ? message : null;
}

const CampaignList = () => {
  const { t } = useTranslation();
  const tBase = "views.outreach.campaigns.index";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const history = useHistory();
  const queryClient = useQueryClient();
  const snackBar = useSnackbarContext();

  const { setModal } = useModalContext();

  // Paged on the server (follow-up 2026-09-02): the list used to fetch every
  // campaign with its full design on each visit. `keepPreviousData` keeps the
  // current page on screen while the next one loads.
  const [page, setPage] = useState(1);
  const {
    data: campaignsPage,
    isLoading,
    isError,
  } = useQuery<CampaignsPage>(
    ["campaigns", page],
    () => getCampaignsPage(page, CAMPAIGNS_PAGE_SIZE),
    { keepPreviousData: true }
  );
  const campaigns = campaignsPage?.rows;
  const pageCount = campaignsPage?.pageCount ?? 1;

  const deleteMutation = useMutation(deleteCampaign, {
    onSuccess: () => {
      // Deleting the only row of the last page would leave an empty page.
      if ((campaignsPage?.rows.length ?? 0) <= 1 && page > 1) {
        setPage(page - 1);
      }
      queryClient.invalidateQueries("campaigns");
      snackBar.showSnackbar(tr("deleteSuccess"));
    },
    onError: () => {
      snackBar.showSnackbar(tr("deleteError"));
    },
  });

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const resetMutation = useMutation(resetCampaign, {
    onSuccess: () => {
      queryClient.invalidateQueries("campaigns");
      snackBar.showSnackbar(tr("resetSuccess"));
    },
    onError: (error: unknown) => {
      // The API explains a refused reset ("N recipients already received
      // it… Clone the campaign"); a generic "failed to reset" would hide the
      // one thing the user needs to know.
      snackBar.showSnackbar(apiErrorMessage(error) ?? tr("resetError"));
    },
  });

  const cloneMutation = useMutation(cloneCampaign, {
    onSuccess: (cloned) => {
      queryClient.invalidateQueries("campaigns");
      snackBar.showSnackbar(tr("cloneSuccess"));
      history.push(`/outreach/campaigns/${cloned.id}/edit`);
    },
    onError: () => {
      snackBar.showSnackbar(tr("cloneError"));
    },
  });

  const formatDate = (dateString: string | null) => {
    if (!dateString) {
      return "--";
    }
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderActions = (campaign: ICampaign) => {
    switch (campaign.status) {
      case "draft":
        return (
          <div className="flex items-center space-x-2">
            <SubtleButton
              primary
              size="small"
              style={{ minWidth: 100, justifyContent: "flex-start" }}
              onClick={() =>
                history.push(`/outreach/campaigns/${campaign.id}/edit`)
              }
            >
              {tr("edit")}
            </SubtleButton>
            <SubtleButton
              primary
              size="small"
              onClick={() =>
                setModal({
                  label: tr("clone"),
                  component: (
                    <ConfirmActionModal
                      title={tr("cloneConfirmTitle")}
                      message={tr("cloneConfirmMessage")}
                      confirmLabel={tr("clone")}
                      onConfirm={() => cloneMutation.mutate(campaign.id)}
                    />
                  ),
                })
              }
            >
              {tr("clone")}
            </SubtleButton>
            <SubtleButton
              $warning
              size="small"
              onClick={() =>
                setModal({
                  label: tr("delete"),
                  component: (
                    <ConfirmActionModal
                      title={tr("deleteConfirmTitle")}
                      message={tr("deleteConfirmMessage")}
                      confirmLabel={tr("delete")}
                      variant="danger"
                      onConfirm={() => handleDelete(campaign.id)}
                    />
                  ),
                })
              }
            >
              {tr("delete")}
            </SubtleButton>
          </div>
        );
      case "sent":
        return (
          <div className="flex items-center space-x-2">
            <SubtleButton
              primary
              size="small"
              style={{ minWidth: 100, justifyContent: "flex-start" }}
              onClick={() =>
                history.push(`/outreach/campaigns/${campaign.id}/report`)
              }
            >
              {tr("viewReport")}
            </SubtleButton>
            <SubtleButton
              primary
              size="small"
              onClick={() =>
                setModal({
                  label: tr("clone"),
                  component: (
                    <ConfirmActionModal
                      title={tr("cloneConfirmTitle")}
                      message={tr("cloneConfirmMessage")}
                      confirmLabel={tr("clone")}
                      onConfirm={() => cloneMutation.mutate(campaign.id)}
                    />
                  ),
                })
              }
            >
              {tr("clone")}
            </SubtleButton>
          </div>
        );
      case "failed":
        return (
          <div className="flex items-center space-x-2">
            <SubtleButton
              primary
              size="small"
              style={{ minWidth: 100, justifyContent: "flex-start" }}
              onClick={() =>
                history.push(`/outreach/campaigns/${campaign.id}/report`)
              }
            >
              {tr("viewReport")}
            </SubtleButton>
            <SubtleButton
              primary
              size="small"
              onClick={() =>
                setModal({
                  label: tr("retry"),
                  component: (
                    <ConfirmActionModal
                      title={tr("retryConfirmTitle")}
                      message={tr("retryConfirmMessage")}
                      confirmLabel={tr("retry")}
                      variant="danger"
                      onConfirm={() => resetMutation.mutate(campaign.id)}
                    />
                  ),
                })
              }
            >
              {tr("retry")}
            </SubtleButton>
            <SubtleButton
              primary
              size="small"
              onClick={() =>
                setModal({
                  label: tr("clone"),
                  component: (
                    <ConfirmActionModal
                      title={tr("cloneConfirmTitle")}
                      message={tr("cloneConfirmMessage")}
                      confirmLabel={tr("clone")}
                      onConfirm={() => cloneMutation.mutate(campaign.id)}
                    />
                  ),
                })
              }
            >
              {tr("clone")}
            </SubtleButton>
          </div>
        );
      case "sending":
        return <SendingProgress campaignId={campaign.id} />;
      case "scheduled":
        return (
          <SubtleButton
            primary
            size="small"
            onClick={() =>
              history.push(`/outreach/campaigns/${campaign.id}/edit`)
            }
          >
            {tr("edit")}
          </SubtleButton>
        );
      default:
        return null;
    }
  };

  const columns = {
    name: {
      label: tr("colName"),
      style: { fontWeight: 600 },
      cell: (row: ICampaign) => row.name,
    },
    campaignType: {
      label: tr("colType"),
      cell: (row: ICampaign) =>
        row.campaignType === "sms" ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            SMS
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            Email
          </span>
        ),
    },
    subject: {
      label: tr("colContent"),
      cell: (row: ICampaign) =>
        row.campaignType === "sms"
          ? row.messageText
            ? row.messageText.length > 50
              ? `${row.messageText.substring(0, 50)}...`
              : row.messageText
            : "--"
          : row.subject,
    },
    status: {
      label: tr("colStatus"),
      cell: (row: ICampaign) => (
        <div>
          <Status color={STATUS_COLORS[row.status || "draft"] || "180,180,180"}>
            {tr(`status_${row.status || "draft"}`)}
          </Status>
          {row.status === "scheduled" && row.schedule && (
            <div className="text-xs text-gray-500 mt-1 whitespace-nowrap">
              {formatScheduleCell(row.schedule)}
            </div>
          )}
        </div>
      ),
    },
    totalRecipients: {
      label: tr("colRecipients"),
      cell: (row: ICampaign) => row.totalRecipients,
    },
    sentAt: {
      label: tr("colSentDate"),
      cell: (row: ICampaign) => formatDate(row.sentAt),
    },
    actions: {
      label: tr("colActions"),
      disableSort: true,
      disableRowClick: true,
      cell: (row: ICampaign) => renderActions(row),
    },
  };

  // Pass undefined while loading so TableComponent shows skeleton
  const tableData = isLoading ? undefined : isError ? [] : campaigns || [];

  return (
    <SafeArea>
      <FlatContainer
        title={<h2>{tr("title")}</h2>}
        buttons={[
          <Button
            primary
            key="new"
            onClick={() => history.push("/outreach/campaigns/new")}
          >
            + {tr("newCampaign")}
          </Button>,
        ]}
      >
        {isError && (
          <p className="text-red-500 py-4">{tr("loadError")}</p>
        )}
        <Table
          data={tableData}
          columns={columns}
          disableSortAll
          pageCount={pageCount}
          pageIndex={page}
          handlePage={(p: number) => setPage(p)}
          handleClick={(row: ICampaign) => {
            if (row.status === "draft" || row.status === "scheduled") {
              history.push(`/outreach/campaigns/${row.id}/edit`);
            } else if (row.status === "sent" || row.status === "failed") {
              history.push(`/outreach/campaigns/${row.id}/report`);
            }
          }}
        />
      </FlatContainer>
    </SafeArea>
  );
};

export default CampaignList;
